import { Accordion } from "@/components/ui/accordion";
import React from "react";
import { useLocale, useTranslations } from "../../../helpers/translations";
import { generateLocalizedPath } from "../helpers/links";
import { RAILS_ROUTES } from "../helpers/routes";
import { MobileNavSubSection } from "../shared/MobileNavSubSection";
import { NavColumnGroup } from "../shared/NavColumnGroup";
import { NavSubColumn } from "../shared/NavSubColumn";
import { NavSubMenuSection } from "../shared/NavSubMenuSection";

type ShopSubMenuProps = {
  compact?: boolean;
  wrapperComponent: React.ComponentType<{
    title: string;
    children: React.ReactNode;
  }>;
};

export const ShopSubMenu = ({ wrapperComponent, compact }: ShopSubMenuProps) => {
  const t = useTranslations("nav.shop");
  const locale = useLocale();
  const WrapperComponent = wrapperComponent;

  if (!compact) {
    return (
      <WrapperComponent title={t("title")}>
        <NavSubMenuSection
          heading={t("design_templates.header")}
          items={[
            {
              title: t("design_templates.links.manufacturing.title"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGN_TEMPLATES_MANUFACTURING, locale),
            },
            {
              title: t("design_templates.links.assembly.title"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGN_TEMPLATES_ASSEMBLY, locale),
            },
            {
              title: t("design_templates.links.material_handling.title"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGN_TEMPLATES_MATERIAL_HANDLING, locale),
            },
            {
              title: t("design_templates.links.end_of_line_packaging.title"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGN_TEMPLATES_EOL_PACKAGING, locale),
            },
          ]}
          cta={{
            text: t("design_templates.view_more.text"),
            href: generateLocalizedPath(RAILS_ROUTES.DESIGNS_OFFICIAL, locale),
          }}
        />
        <NavColumnGroup heading={t("marketplace.header")}>
          <NavSubColumn
            subtitle={t("marketplace.vention_parts.header")}
            items={[
              {
                title: t("marketplace.vention_parts.links.extrusions.title"),
                description: t("marketplace.vention_parts.links.extrusions.description"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_EXTRUSIONS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.actuators_motors.title"),
                description: t("marketplace.vention_parts.links.actuators_motors.description"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_ACTUATORS_MOTORS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.robot_arms.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_ROBOT_ARMS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.controllers.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_CONTROLLERS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.grippers.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_GRIPPERS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.safety.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_SAFETY, locale),
              },
            ]}
            cta={{
              text: t("marketplace.vention_parts.view_more.text"),
              href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY, locale),
            }}
          />
          <NavSubColumn
            subtitle={t("marketplace.partner_parts.header")}
            items={[
              {
                title: t("marketplace.partner_parts.links.fanuc.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_FANUC, locale),
              },
              {
                title: t("marketplace.partner_parts.links.universal_robots.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_UNIVERSAL_ROBOTS, locale),
              },
              {
                title: t("marketplace.partner_parts.links.onrobot.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_ONROBOT, locale),
              },
              {
                title: t("marketplace.partner_parts.links.schunk.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_SCHUNK, locale),
              },
              {
                title: t("marketplace.partner_parts.links.keyence.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_KEYENCE, locale),
              },
              {
                title: t("marketplace.partner_parts.links.franka_robotics.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_FRANKA_ROBOTICS, locale),
              },
            ]}
            cta={{
              text: t("marketplace.partner_parts.view_more.text"),
              href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY, locale),
            }}
          />
        </NavColumnGroup>
      </WrapperComponent>
    );
  }

  return (
    <WrapperComponent title={t("title")}>
      <Accordion type="multiple" className="w-full border-none">
        <MobileNavSubSection
          title={t("design_templates.header")}
          items={[
            {
              title: t("design_templates.links.manufacturing.title"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGN_TEMPLATES_MANUFACTURING, locale),
            },
            {
              title: t("design_templates.links.assembly.title"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGN_TEMPLATES_ASSEMBLY, locale),
            },
            {
              title: t("design_templates.links.material_handling.title"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGN_TEMPLATES_MATERIAL_HANDLING, locale),
            },
            {
              title: t("design_templates.links.end_of_line_packaging.title"),
              href: generateLocalizedPath(RAILS_ROUTES.DESIGN_TEMPLATES_EOL_PACKAGING, locale),
            },
          ]}
          cta={{
            text: t("design_templates.view_more.text"),
            href: generateLocalizedPath(RAILS_ROUTES.DESIGNS_OFFICIAL, locale),
          }}
        />
        <MobileNavSubSection title={t("marketplace.header")}>
          <MobileNavSubSection
            title={t("marketplace.vention_parts.header")}
            titleClassName="text-tertiary hover:text-secondary data-[state=open]:text-primary"
            items={[
              {
                title: t("marketplace.vention_parts.links.extrusions.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_EXTRUSIONS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.actuators_motors.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_ACTUATORS_MOTORS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.robot_arms.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_ROBOT_ARMS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.controllers.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_CONTROLLERS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.grippers.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_GRIPPERS, locale),
              },
              {
                title: t("marketplace.vention_parts.links.safety.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_SAFETY, locale),
              },
            ]}
            cta={{
              text: t("marketplace.vention_parts.view_more.text"),
              href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY, locale),
            }}
          />
          <MobileNavSubSection
            title={t("marketplace.partner_parts.header")}
            titleClassName="text-tertiary hover:text-secondary data-[state=open]:text-primary"
            items={[
              {
                title: t("marketplace.partner_parts.links.fanuc.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_FANUC, locale),
              },
              {
                title: t("marketplace.partner_parts.links.universal_robots.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_UNIVERSAL_ROBOTS, locale),
              },
              {
                title: t("marketplace.partner_parts.links.onrobot.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_ONROBOT, locale),
              },
              {
                title: t("marketplace.partner_parts.links.schunk.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_SCHUNK, locale),
              },
              {
                title: t("marketplace.partner_parts.links.keyence.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_KEYENCE, locale),
              },
              {
                title: t("marketplace.partner_parts.links.franka_robotics.title"),
                href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY_FRANKA_ROBOTICS, locale),
              },
            ]}
            cta={{
              text: t("marketplace.partner_parts.view_more.text"),
              href: generateLocalizedPath(RAILS_ROUTES.PARTS_LIBRARY, locale),
            }}
          />
        </MobileNavSubSection>
      </Accordion>
    </WrapperComponent>
  );
};
